import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';

import type { EventView } from '@vlabs/monokit/entities/events/api';
import { useTableSettingsContext } from '@vlabs/monokit/entities/events/context/TableSettingsContext';
import type { EventListPermissions } from '@vlabs/monokit/entities/events/event-list-table/EventListPermissions';
import { EmptyCard } from '@vlabs/monokit/shared/ui/empty-card';
import { TableView } from '@vlabs/monokit/shared/ui/table-view';

import {
  renderCell, renderHeader,
  attributesCell, eventInfoCell,
  sampleCell, topMatchCell,
  AttributesCellPoint,
  EventInfoCell, SampleCell,
  TopMatchCardCell,
} from './cells';
import { useEventListTableViewTranslation } from './i18n';
import st from './shared.module.sass';

interface EventListTableViewProps {
  data: EventView[];
  permissions?: Partial<EventListPermissions>;
}

function EventListTableView({
  data,
  permissions = {},
}: EventListTableViewProps): React.JSX.Element {
  const { t } = useEventListTableViewTranslation();
  const { settings } = useTableSettingsContext();

  const columns: ColumnDef<EventView>[] = useMemo(() => [
    {
      id: 'sample',
      header: renderHeader(sampleCell, t('sample')),
      cell: renderCell(sampleCell, (ctx) => {
        const event = ctx.row.original as unknown as EventView;
        return event.event_id
          ? (
            <SampleCell
              eventId={event.event_id}
              permissions={permissions}
            />
          )
          : (
            <EmptyCard
              className={st.EmptyCard}
              text={t('no_data')}
            />
          );
      }),
    },
    {
      id: 'top_match',
      header: renderHeader(topMatchCell, t('top_match')),
      cell: renderCell(topMatchCell, (ctx) => {
        const { top_match: topMatch } = ctx.row.original as unknown as EventView;

        if (!topMatch || !('face_id' in topMatch)) {
          return (
            <EmptyCard
              className={st.NoMatch}
              dataTip={t('no_match')}
              text={t('no_data')}
            />
          );
        }
        return (
          <TopMatchCardCell
            permissions={permissions}
            topMatch={topMatch}
          />
        );
      }),
    },
    {
      id: 'info',
      header: renderHeader(eventInfoCell, t('info')),
      cell: renderCell(eventInfoCell, (ctx) => (
        <EventInfoCell
          event={ctx.row.original as unknown as EventView}
          handlerRoute="/handlers/:handlerId"
          listRoute="/lists/:listId"
          permissions={permissions}
        />
      )),
    },
    {
      id: 'attributes',
      header: renderHeader(attributesCell, t('attributes')),
      cell: renderCell(attributesCell, (ctx) => (
        <AttributesCellPoint event={ctx.row.original as never} />
      )),
    },
  ] as ColumnDef<EventView>[], [permissions, t]);

  return (
    <TableView
      className={st.EventListTable}
      columns={columns}
      data={data}
      getRowId={(event) => event.event_id ?? ''}
      state={{
        columnVisibility: {
          top_match: settings.isTopMatchVisible,
        },
      }}
    />
  );
}

export { EventListTableView };
